import { useEffect, useRef, useCallback } from 'react';

const TYPING_INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds

/**
 * Call resetSession on typing events that happen after 10+ minutes since last typing
 */
export const useTypingActivityDetection = (
  resetSession: () => Promise<void>,
  isLoggedIn: boolean,
) => {
  const canResetSession = useRef(true);
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTypingTimer = useCallback(() => {
    if (typingTimer.current) {
      clearTimeout(typingTimer.current);
      typingTimer.current = null;
    }
  }, []);

  const resetTypingTimer = useCallback(() => {
    clearTypingTimer();

    typingTimer.current = setTimeout(() => {
      canResetSession.current = true;
    }, TYPING_INACTIVITY_TIMEOUT);
  }, [clearTypingTimer]);

  useEffect(() => {
    if (!isLoggedIn) {
      canResetSession.current = true;
      clearTypingTimer();
    }
  }, [isLoggedIn, clearTypingTimer]);

  useEffect(() => {
    if (!isLoggedIn) return;

    const handleTyping = () => {
      if (canResetSession.current) {
        canResetSession.current = false;
        resetSession();
      }

      resetTypingTimer();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event?.key?.length === 1 ||
        event?.key === 'Backspace' ||
        event?.key === 'Delete' ||
        event?.key === 'Enter' ||
        event?.key === 'Tab'
      ) {
        handleTyping();
      }
    };

    const handleInput = () => {
      handleTyping();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('input', handleInput);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('input', handleInput);
      clearTypingTimer();
    };
  }, [isLoggedIn, resetSession, resetTypingTimer, clearTypingTimer]);
};
